import React, { useState, useEffect } from 'react';
import { privateAPI } from '../util/api';
import { useParams } from 'react-router-dom';

const WebhookLogs = () => {
  const { webhookId } = useParams();
  const [logs, setLogs] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState({ name: '', id: webhookId });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const logsPerPage = 10;

  useEffect(() => {
    const fetchWebhookInfo = async () => {
      try {
        const response = await privateAPI.get(`/webhook/info/${webhookId}`);
        setWebhookInfo({ name: response.data.name, id: webhookId });
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to fetch webhook details.');
      }
    };

    const fetchWebhookLogs = async () => {
      setLoading(true);
      try {
        const response = await privateAPI.get(`/logs/${webhookId}?limit=${logsPerPage}&skip=${(currentPage - 1) * logsPerPage}`);
        const { total, logs } = response.data;

        setLogs(logs);
        setTotalLogs(total);
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to fetch logs.');
      } finally {
        setLoading(false);
      }
    };

    fetchWebhookInfo();
    fetchWebhookLogs();
  }, [webhookId, currentPage]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage * logsPerPage < totalLogs) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-screen-xl">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-semibold mb-2">Webhook Logs</h1>
        {webhookInfo.name ? (
          <p className="text-gray-600">
            <span className="font-medium text-gray-700">Webhook Name:</span> {webhookInfo.name} |
            <span className="font-medium text-gray-700"> Webhook ID:</span> {webhookInfo.id}
          </p>
        ) : (
          <p className="text-gray-600">Loading webhook details...</p>
        )}
      </div>

      {loading && <p>Loading logs...</p>}

      {error && (
        <div className="text-red-500 mb-4 text-center">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="relative overflow-x-auto max-w-screen-xl mx-auto">
            <table className="w-full text-sm text-left text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                <tr>
                  <th className="px-6 py-3">Log ID</th>
                  <th className="px-6 py-3">Message</th>
                  <th className="px-6 py-3">Service Response</th>
                  <th className="px-6 py-3">Received At</th>
                  <th className="px-6 py-3">Delivered At</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id} className="bg-white border-b">
                    <td className="px-6 py-4">{log._id}</td>
                    <td className="px-6 py-4">{log.message}</td>
                    <td className="px-6 py-4">{log.service_response}</td>
                    <td className="px-6 py-4">{new Date(log.received_at).toLocaleString()}</td>
                    <td className="px-6 py-4">{new Date(log.delivered_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-center items-center mt-6 gap-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
            >
              {`<<<`}
            </button>
            <span>
              Page {currentPage} of {totalLogs > 0 ? Math.ceil(totalLogs / logsPerPage) : 1}
            </span>
            <button
              className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50"
              onClick={handleNextPage}
              disabled={currentPage * logsPerPage >= totalLogs}
            >
              {`>>>`}
            </button>
          </div>
        </>
      )}

      {!loading && !error && logs.length === 0 && <p className="text-center">No logs found for this webhook.</p>}
    </div>
  );
};

export default WebhookLogs;